import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useUserContext } from "../../../../providers/UserProvider";
import { useTeamsEligibleProducts } from "../../../../hooks/useTeamsEligibleProducts/useTeamsEligibleProducts";
import { ProductDropdown } from "../../ProductDropdown/ProductDropdown";
import { InstanceDropdown } from "../../InstanceDropdown/InstanceDropdown";
import { IOrganizationProductInstance, OrganizationProduct } from "../../../../domain/OrganizationProduct";

export type UserGroupProductsProps = {
    instanceId?: string;
    saving?: boolean;
    onSelection: ({ productId, instanceId }: { productId: string; instanceId: string }) => void;
};

const DEFAULT_PRODUCT_ID = "";
const DEFAULT_INSTANCE_ID = "";

type AutoSelectState = {
    hasAutoSelectedProduct: boolean;
    hasAutoSelectedInstance: boolean;
};

type AutoSelectAction =
    | { type: "PRODUCT_AUTO_SELECTED" }
    | { type: "INSTANCE_AUTO_SELECTED" }
    | { type: "RESET_PRODUCT_AUTO_SELECT" }
    | { type: "RESET_INSTANCE_AUTO_SELECT" };

const autoSelectReducer = (state: AutoSelectState, action: AutoSelectAction): AutoSelectState => {
    switch (action.type) {
        case "PRODUCT_AUTO_SELECTED":
            return { ...state, hasAutoSelectedProduct: true };
        case "INSTANCE_AUTO_SELECTED":
            return { ...state, hasAutoSelectedInstance: true };
        case "RESET_PRODUCT_AUTO_SELECT":
            return { ...state, hasAutoSelectedProduct: false };
        case "RESET_INSTANCE_AUTO_SELECT":
            return { ...state, hasAutoSelectedInstance: false };
        default:
            return state;
    }
};

export const ProductInstanceSelection = ({
    instanceId: preselectedInstanceId,
    saving = false,
    onSelection
}: UserGroupProductsProps) => {
    const { organizationId } = useUserContext();
    const { eligibleProducts, isLoading: instancesLoading } = useTeamsEligibleProducts({ organizationId });

    const [productId, setProductId] = useState(DEFAULT_PRODUCT_ID);
    const [instanceId, setInstanceId] = useState(DEFAULT_INSTANCE_ID);
    const [autoSelectState, dispatch] = useReducer(autoSelectReducer, {
        hasAutoSelectedProduct: false,
        hasAutoSelectedInstance: false
    });

    const instanceOptions = useMemo(() => {
        return (eligibleProducts ?? []).flatMap((product) =>
            product.instances.filter((instance) => instance.optiIdEnabled && (!productId || product.id === productId))
        );
    }, [eligibleProducts, productId]);

    const hasInstances = instanceOptions.length > 0;

    const notifySelection = useCallback(
        (productId: string, instanceId: string) => {
            onSelection({ productId, instanceId });
        },
        [onSelection]
    );

    useEffect(() => {
        if (!autoSelectState.hasAutoSelectedProduct && !productId && eligibleProducts?.length === 1) {
            setProductId(eligibleProducts[0].id);
            dispatch({ type: "PRODUCT_AUTO_SELECTED" });
        }

        if (
            !autoSelectState.hasAutoSelectedInstance &&
            productId &&
            !instanceId &&
            !instancesLoading &&
            instanceOptions.length === 1
        ) {
            const instance = instanceOptions[0];
            setInstanceId(instance.id);
            notifySelection(productId, instance.id);
            dispatch({ type: "INSTANCE_AUTO_SELECTED" });
        }

        if (eligibleProducts?.length !== 1 && autoSelectState.hasAutoSelectedProduct && !productId) {
            dispatch({ type: "RESET_PRODUCT_AUTO_SELECT" });
        }
    }, [
        eligibleProducts,
        productId,
        instanceId,
        instanceOptions,
        instancesLoading,
        autoSelectState.hasAutoSelectedProduct,
        autoSelectState.hasAutoSelectedInstance,
        notifySelection
    ]);

    useEffect(() => {
        if (preselectedInstanceId && !instanceId) {
            const matchingInstanceDetails = instanceOptions.find((instance) => instance.id === preselectedInstanceId);
            const matchingProduct = eligibleProducts?.find((product) =>
                product.instances.find((instance) => instance.id === preselectedInstanceId)
            );
            if (matchingInstanceDetails && matchingProduct) {
                setProductId(matchingProduct.id);
                setInstanceId(matchingInstanceDetails.id);
                notifySelection(matchingProduct.id, matchingInstanceDetails.id);
            }
        }
    }, [eligibleProducts, instanceId, preselectedInstanceId, instanceOptions, notifySelection]);

    const handleProductChange = ({ product }: { product: OrganizationProduct }) => {
        setProductId(product.id);
        setInstanceId(DEFAULT_INSTANCE_ID);
        notifySelection(product.id, DEFAULT_INSTANCE_ID);
        dispatch({ type: "RESET_INSTANCE_AUTO_SELECT" });
    };

    const handleInstanceChange = ({
        value
    }: {
        value: IOrganizationProductInstance | IOrganizationProductInstance[] | null;
    }) => {
        if (value && !Array.isArray(value)) {
            setInstanceId(value.id);
            notifySelection(productId, value.id);
        } else {
            setInstanceId(DEFAULT_INSTANCE_ID);
        }
    };

    return (
        <>
            <div className="push-double--top push-double--bottom">
                <ProductDropdown
                    products={eligibleProducts || []}
                    value={productId}
                    onChange={handleProductChange}
                    disabled={!hasInstances || saving}
                    placeholder={eligibleProducts?.length ? "Select a product..." : "No available products"}
                    white
                    fullWidth
                    required
                />
            </div>

            <div>
                <InstanceDropdown
                    instances={instanceOptions}
                    value={instanceId}
                    onChange={handleInstanceChange}
                    disabled={!productId || instancesLoading || saving}
                    loading={instancesLoading}
                    placeholder={instanceOptions.length ? "Select a product instance..." : "No available instances"}
                    white
                    fullWidth
                    required
                />
            </div>
        </>
    );
};

ProductInstanceSelection.displayName = "ProductInstanceSelection";
